"use client";

import {
	useState,
	useRef,
	useEffect,
	type MouseEvent,
	type SyntheticEvent,
} from "react";
import {Plus, Minus} from "lucide-react";
import {Typography} from "@repo/ui/aops-components/typography";
import type {
	FullCourse,
	FullCourseOffering,
	CoursePageSection,
} from "@repo/sanity-utils/query-types";
import styles from "./SequenceSelection.module.css";
import {
	courseOfferingSelectionsAtom,
	learningFormatAtom,
} from "@/app/atoms/courseSelections";
import {
	findOfferingKeyForPart,
	findPartSlugForOfferingKey,
	getSequenceParts,
	resolveSelectedOfferingKey,
	toSequencePartSlug,
} from "@/lib/activeCourseOffering";
import {useAtom} from "jotai";
import {useIsMobile} from "@repo/ui/hooks/use-mobile";
import {SelectableTile} from "../SelectableTile";
import {CourseSection} from "@/app/components/CourseSection";
import {getKeyedCopyText} from "@repo/utils/keyedCopy";
import {stegaClean} from "next-sanity";
import {
	COURSE_SECTION_IDS,
	scrollToCourseSection,
	shouldAutoScrollOnSelection,
} from "../scrollToCourseSection";
import {shouldApplyUrlPreference} from "../courseSelectionLogic";
import {sendDiscoveryEventBeacon} from "@/lib/discoveryEventBeacon";

type Course = NonNullable<FullCourse>;

type SequenceButtonProps = {
	courseOffering: FullCourseOffering;
	courseSlug: string;
	sequenceSubtitle?: string | null;
	sequenceDescription?: string;
	topicsCovered?: string[];
	/** When true, always emit a topics Content row so peer cards share height */
	showTopicsRow: boolean;
	isSelected: boolean;
	onSelect: (key: string) => void;
	/** When false, only the Select button selects (mobile). */
	cardSelectable: boolean;
};

const TITLE_FALLBACK = "{{courseTitle}} is offered in two parts";
const TITLE_STANDALONE_KEY = "title-standalone";
const BODY_STANDALONE_KEY = "body-standalone";
const SUBTITLE_FALLBACK =
	"If you're new to this course, we recommend starting with Part 1.";
const STEP_TAG_KEY = "step-tag";
const FALLBACK_STEP_TAG = "STEP 2";

function replaceCourseTitle(value: string, courseTitle: string) {
	return stegaClean(value).replaceAll(
		"{{courseTitle}}",
		stegaClean(courseTitle)
	);
}

/**
 * Get text from the keyed copy or use the fallback text
 */
function getSequenceSectionText({
	defaultText,
	fallbackText,
	standaloneKey,
	sequenceType,
	keyedCopy,
	courseTitle,
}: {
	defaultText: string | null | undefined;
	fallbackText: string;
	standaloneKey: string;
	sequenceType: Course["sequenceType"];
	keyedCopy: {key: string; text: string}[];
	courseTitle: string;
}): string {
	const defaultValue = defaultText?.trim() || fallbackText;
	const value =
		sequenceType === "standalone"
			? getKeyedCopyText(keyedCopy, standaloneKey) || defaultValue
			: defaultValue;

	return replaceCourseTitle(value, courseTitle);
}

function TopicsCovered({
	topicsCovered,
	cardSelectable,
}: {
	topicsCovered: string[];
	cardSelectable: boolean;
}) {
	const [topicsExpanded, setTopicsExpanded] = useState(false);
	const hasTopics = (topicsCovered?.length ?? 0) > 0;
	if (!hasTopics) return;

	const Topic = (topic: string) => (
		<li key={topic} className={styles.tag}>
			<Typography variant="tag" as="span">
				{topic}
			</Typography>
		</li>
	);

	const toggleTopics = (e: MouseEvent) => {
		// Mobile-only expand/collapse; on desktop let the click select the card
		if (cardSelectable) return;
		e.stopPropagation();
		setTopicsExpanded((prev) => !prev);
	};
	return (
		<div
			className={`${styles.topicsSection} ${styles.topicsSectionCollapsible}`}
			onClick={toggleTopics}
		>
			<div className={styles.topicsHeader}>
				<Typography variant="body" as="span">
					Core topics:
				</Typography>

				<span className={styles.topicsToggle} aria-hidden>
					{topicsExpanded ? (
						<Minus size={24} strokeWidth={1.5} />
					) : (
						<Plus size={24} strokeWidth={1.5} />
					)}
				</span>
			</div>
			<ul
				className={`${styles.tagList} ${topicsExpanded ? styles.tagListExpanded : ""}`}
				role="list"
			>
				{topicsCovered?.map(Topic)}
			</ul>
		</div>
	);
}

function SequenceButton({
	sequenceSubtitle,
	courseOffering,
	courseSlug,
	sequenceDescription,
	topicsCovered,
	showTopicsRow,
	isSelected,
	onSelect,
	cardSelectable,
}: SequenceButtonProps) {
	const [topicsExpanded, setTopicsExpanded] = useState(false);

	const select = () => {
		// SelectableTile.SelectButton (CourseButton) stops propagation internally,
		// so a click on it never reaches CourseSection's onSelect below —
		// track here, the one place every selection path (tile click or the
		// Select button itself) actually runs through.
		if (courseOffering.sequenceLabel) {
			sendDiscoveryEventBeacon("sequence_selected", {
				course: courseSlug,
				modality: courseOffering.modality.slug.current,
				sequence: courseOffering.sequenceLabel,
				auto_select: false,
			});
		}
		onSelect(courseOffering._key);
	};

	const expandOnMobile = (e: SyntheticEvent) => {
		if (cardSelectable) return;
		e.stopPropagation();
		setTopicsExpanded(true);
	};

	return (
		<SelectableTile
			isSelected={isSelected}
			onSelect={cardSelectable ? select : undefined}
		>
			{/* Own subgrid row so header heights align across columns */}
			<SelectableTile.Content>
				{/* Header — acts as expand trigger on mobile */}
				<div
					className={`${styles.cardHeader} ${!topicsExpanded ? styles.cardHeaderCollapsible : ""}`}
					role={!topicsExpanded && !cardSelectable ? "button" : undefined}
					tabIndex={!topicsExpanded && !cardSelectable ? 0 : undefined}
					onClick={!topicsExpanded ? expandOnMobile : undefined}
					onKeyDown={
						!topicsExpanded
							? (e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										expandOnMobile(e);
									}
								}
							: undefined
					}
					aria-expanded={topicsExpanded || undefined}
				>
					<Typography variant="h4" as="h3">
						{courseOffering.sequenceLabel}
					</Typography>
					<Typography variant="subtitle">{sequenceSubtitle}</Typography>
					{sequenceDescription && (
						<Typography
							variant="body"
							className={!topicsExpanded ? styles.descriptionClamped : ""}
						>
							{sequenceDescription}
						</Typography>
					)}
				</div>
			</SelectableTile.Content>

			{showTopicsRow && (
				<SelectableTile.Content className={styles.topicsContent}>
					<TopicsCovered
						topicsCovered={topicsCovered ?? []}
						cardSelectable={cardSelectable}
					/>
				</SelectableTile.Content>
			)}

			<SelectableTile.SelectButton isSelected={isSelected} onClick={select} />
		</SelectableTile>
	);
}

export type SequenceSelectionProps = {
	course: Course;
	coursePageSection?: NonNullable<CoursePageSection>;
	initialPart?: string | null;
};

// TODO: Hide this section if the course offerings don't actually have two "sequential" parts
export function SequenceSelection({
	course: {
		_id: courseId,
		slug,
		title,
		courseOfferings,
		sequenceDescriptions = [],
		sequenceType,
	},
	coursePageSection,
	initialPart = null,
}: SequenceSelectionProps) {
	const [selectedLearningFormat] = useAtom(learningFormatAtom);
	const [selectionsByCourse, setSelectionsByCourse] = useAtom(
		courseOfferingSelectionsAtom
	);
	const appliedCourseIdRef = useRef<string | null>(null);
	const appliedUrlPartRef = useRef<string | null>(null);

	const partsForModality = getSequenceParts({
		courseOfferings,
		selectedModalityId: selectedLearningFormat,
	});
	// Derive only — never write jotai during render (CTA also reads this atom).
	const resolvedOfferingKey = resolveSelectedOfferingKey({
		courseId,
		courseOfferings,
		selectedModalityId: selectedLearningFormat,
		selectionsByCourse,
		initialPart,
	});

	// Seed `?part=` into jotai once per course / URL change (same apply-once
	// rules as modality) so deep links override a stale session without
	// permanently locking the selection while the param stays in the URL.
	useEffect(() => {
		const previousCourseId = appliedCourseIdRef.current;
		const urlPart = initialPart ?? null;
		const shouldApply = shouldApplyUrlPreference({
			previousCourseId,
			courseId,
			urlSlug: urlPart,
			appliedUrlSlug: appliedUrlPartRef.current,
		});

		if (!shouldApply) {
			return;
		}

		// Wait until modality is known so we can resolve the part for that set.
		if (!selectedLearningFormat) {
			return;
		}

		const trimmedUrl = urlPart?.trim() || null;
		appliedCourseIdRef.current = courseId;
		appliedUrlPartRef.current = trimmedUrl;

		if (!trimmedUrl) {
			return;
		}

		const parts = getSequenceParts({
			courseOfferings,
			selectedModalityId: selectedLearningFormat,
		});
		const offeringKey = findOfferingKeyForPart(parts, trimmedUrl);
		if (!offeringKey) {
			return;
		}

		if (offeringKey !== resolvedOfferingKey) {
			const offering = courseOfferings.find(
				(candidate) => candidate._key === offeringKey
			);
			if (offering?.sequenceLabel) {
				sendDiscoveryEventBeacon("sequence_selected", {
					course: slug.current,
					modality: offering.modality.slug.current,
					sequence: offering.sequenceLabel,
					auto_select: true,
				});
			}
		}

		setSelectionsByCourse((current) => ({
			...current,
			[courseId]: {
				offeringKey,
				preferredPartSlug: toSequencePartSlug(trimmedUrl),
			},
		}));
	}, [
		courseId,
		courseOfferings,
		initialPart,
		resolvedOfferingKey,
		selectedLearningFormat,
		setSelectionsByCourse,
		slug,
	]);

	const partAutoScrollAllowedRef = useRef(resolvedOfferingKey == null);
	const didPartAutoScrollRef = useRef(false);
	const scrollGateModalityRef = useRef(selectedLearningFormat);

	useEffect(() => {
		// Reset once-flags when modality changes (Sequence remounts its section by key).
		if (scrollGateModalityRef.current !== selectedLearningFormat) {
			scrollGateModalityRef.current = selectedLearningFormat;
			partAutoScrollAllowedRef.current = resolvedOfferingKey == null;
			didPartAutoScrollRef.current = false;
		}
	}, [selectedLearningFormat, resolvedOfferingKey]);

	// On phone, only the Select button selects — same interaction model as LearningFormat mobile.
	const cardSelectable = useIsMobile() !== true;

	// Get the title and subtitle from the keyed copy
	const keyedCopy = coursePageSection?.keyedCopy ?? [];
	const sectionTitle = getSequenceSectionText({
		defaultText: coursePageSection?.title,
		fallbackText: TITLE_FALLBACK,
		standaloneKey: TITLE_STANDALONE_KEY,
		sequenceType,
		keyedCopy,
		courseTitle: title,
	});
	const sectionSubtitle = getSequenceSectionText({
		defaultText: coursePageSection?.body,
		fallbackText: SUBTITLE_FALLBACK,
		standaloneKey: BODY_STANDALONE_KEY,
		sequenceType,
		keyedCopy,
		courseTitle: title,
	});
	const stepTag =
		getKeyedCopyText(keyedCopy, STEP_TAG_KEY) || FALLBACK_STEP_TAG;
	const selectOffering = (key: string) => {
		if (resolvedOfferingKey === key) {
			return;
		}

		setSelectionsByCourse((current) => ({
			...current,
			[courseId]: {
				offeringKey: key,
				preferredPartSlug: findPartSlugForOfferingKey(courseOfferings, key),
			},
		}));

		const shouldScroll = shouldAutoScrollOnSelection({
			allowAutoScroll: partAutoScrollAllowedRef.current,
			alreadyScrolled: didPartAutoScrollRef.current,
			previousSelectedId: resolvedOfferingKey,
			nextSelectedId: key,
		});
		partAutoScrollAllowedRef.current = false;

		if (!shouldScroll) {
			return;
		}

		didPartAutoScrollRef.current = true;
		scrollToCourseSection(COURSE_SECTION_IDS.cta);
	};

	// Versions use the CTA card for schedule links instead of part selection.
	if (sequenceType === "versions") {
		return null;
	}

	if (!selectedLearningFormat) return null;

	if (partsForModality.length < 2) return null;

	const showTopicsRow = partsForModality.some((offering) => {
		const seqDesc = sequenceDescriptions?.find(
			(d) => d.sequenceLabel === offering.sequenceLabel
		);
		return (seqDesc?.topicsCovered?.length ?? 0) > 0;
	});
	// header + optional topics + select
	const tileRows = 2 + (showTopicsRow ? 1 : 0);

	const options = partsForModality.map((offering: FullCourseOffering) => {
		const {_key, sequenceLabel} = offering;
		const seqDesc = sequenceDescriptions?.find(
			(d) => d.sequenceLabel === sequenceLabel
		);
		return {
			value: _key,
			body: (
				<SequenceButton
					courseOffering={offering}
					courseSlug={slug.current}
					sequenceSubtitle={
						seqDesc?.sequenceSubheader
							? replaceCourseTitle(seqDesc.sequenceSubheader, title)
							: seqDesc?.sequenceSubheader
					}
					sequenceDescription={seqDesc?.sequenceDescription}
					topicsCovered={seqDesc?.topicsCovered ?? []}
					showTopicsRow={showTopicsRow}
					isSelected={resolvedOfferingKey === _key}
					onSelect={selectOffering}
					cardSelectable={cardSelectable}
				/>
			),
		};
	});

	return (
		<CourseSection
			key={selectedLearningFormat}
			id="sequence"
			title={sectionTitle}
			subtitle={sectionSubtitle}
			stepTag={stepTag}
			options={options}
			tileRows={tileRows}
			defaultValue={resolvedOfferingKey}
			onSelect={
				cardSelectable ? (value) => selectOffering(value as string) : undefined
			}
		/>
	);
}
